import { inject } from '@patsnap/tahiti-core/shared/inversify';
import type { SpecificationStreamContent, SpecificationStreamProvider } from '../../types/specification';
import { SpecificationGenParagraphApi } from '../../apis/gen-specification-paragraph';
import { safeToString, SSE2StreamProvider } from '../../utils';
import { SpecificationOperateType } from '../../enums';
import { AbstractBaseSection } from './abstract-base-section';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import { UpdateTaskContentApi } from '@/infrastructure/api-functions/core-drafting-api/update-task-content-api';
import type { SpecificationParagraphWithData } from '../../types/specification-gen-content';

export abstract class CommonSection extends AbstractBaseSection {
    private _currentProvider: SpecificationStreamProvider<SpecificationStreamContent<string>> | undefined;

    constructor(
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
        @inject(SpecificationGenParagraphApi)
        private readonly _genParagraphApi: SpecificationGenParagraphApi,
        @inject(UpdateTaskContentApi)
        private _updateTaskContentApi: UpdateTaskContentApi,
    ) {
        super();
    }

    async generate(regenerate?: boolean) {
        this.stop();

        const sse = this._genParagraphApi.genSpecificationParagraph({
            task_id: this._currentTaskService.taskId,
            content_type: this.contentType,
            operate_type: regenerate ? SpecificationOperateType.Regenerate : undefined,
        });

        const provider = SSE2StreamProvider<string, string>(
            sse,
            (content) => content || '',
        );

        this._currentProvider = provider;

        await provider.subscribe(({ status, content }) => {
            this.updateContent({
                type: this.contentType,
                content: safeToString(content),
                status: status!,
            });
        }).promise;

        return this.currentContent!;
    }

    async save(paragraph: SpecificationParagraphWithData) {
        return this._updateTaskContentApi.updateTaskContent({
            taskId: this._currentTaskService.taskId,
            contentType: paragraph.type,
            content: paragraph.content || '',
        });
    }

    stop() {
        if (this._currentProvider) {
            this._currentProvider.abort();
            this._currentProvider = undefined;
            this.currentContent = undefined;
        }
    }
}
